import { useQueryClient } from '@tanstack/react-query';
import { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackClickedEditCustomField } from '@air/analytics';
import { type BoardCustomFieldValueListResponse } from '@air/api';
import { type AssetCustomFieldValue, type BoardCustomFieldValue } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { CustomFieldDeleteModal } from '~/components/CustomFields/CustomFieldDeleteModal';
import { CustomFieldsEditModal } from '~/components/CustomFields/CustomFieldsEditModal';
import {
  getAssetCustomFieldsKey,
  getCustomFieldOptionsKey,
  getPrivateBoardCustomFieldsKey,
} from '~/constants/react-query-keys';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { centralizedClipAssetIdSelector } from '~/store/centralizedClip/selectors';
import { removeFieldFromConfigurableCurrentAndSavedView } from '~/store/configViews/actions';
import { useDeleteWorkspaceCustomField } from '~/swr-hooks/customFields/useDeleteWorkspaceCustomField';
import { canDeleteCustomFields, canEditCustomFields } from '~/utils/permissions/workspacePermissions';
import { PanelCustomFieldContainer, type PanelCustomFieldContainerProps } from './PanelCustomFieldContainer';

export type PrivateAssetPanelCustomFieldContainerProps = Omit<
  PanelCustomFieldContainerProps<AssetCustomFieldValue>,
  'canDeleteCustomFields' | 'canEditCustomFields' | 'onClick'
>;

export const PrivateAssetPanelCustomFieldContainer = memo(
  ({ field, ...rest }: PrivateAssetPanelCustomFieldContainerProps) => {
    const [showCustomFieldEditModal] = useAirModal(CustomFieldsEditModal);
    const [showDeleteModal] = useAirModal(CustomFieldDeleteModal);
    const { deleteCustomField } = useDeleteWorkspaceCustomField();
    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const canEdit = canEditCustomFields(permissions);
    const canDelete = canDeleteCustomFields(permissions);
    const assetId = useSelector(centralizedClipAssetIdSelector);
    const boardId = useURLBoardIdSelector();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { trackClickedEditCustomField } = useTrackClickedEditCustomField();

    const fieldKey = getCustomFieldOptionsKey(field.id);
    const allFieldsKey = getAssetCustomFieldsKey(assetId);

    const handleDelete = () => {
      queryClient.invalidateQueries({ queryKey: fieldKey });
      if (boardId) {
        queryClient.setQueryData<BoardCustomFieldValueListResponse | undefined>(
          getPrivateBoardCustomFieldsKey(boardId),
          (fields) =>
            fields
              ? {
                  ...fields,
                  data: fields.data.filter((f: BoardCustomFieldValue) => f.id !== field.id),
                  total: fields.total - 1,
                }
              : undefined,
        );

        queryClient.invalidateQueries({ queryKey: getPrivateBoardCustomFieldsKey(boardId) });
      }

      dispatch(removeFieldFromConfigurableCurrentAndSavedView({ fieldId: field.id }));

      queryClient.invalidateQueries({ queryKey: allFieldsKey });
    };

    const onClick = () => {
      if (canEdit) {
        trackClickedEditCustomField({ location: 'asset-details', type: field.type.title });
        showCustomFieldEditModal({
          field,
          trackingLocation: 'asset-details',
          onSaveChanges: () => {
            queryClient.invalidateQueries({ queryKey: fieldKey });
            queryClient.invalidateQueries({ queryKey: allFieldsKey });
          },
          onDelete: handleDelete,
        });
      } else if (canDelete) {
        showDeleteModal({
          handleConfirmDeleteClick: () =>
            deleteCustomField.mutateAsync(
              {
                customFieldId: field.id,
                trackingLocation: 'asset-details',
              },
              {
                onSuccess: handleDelete,
              },
            ),
        });
      }
    };

    return (
      <PanelCustomFieldContainer
        canDeleteCustomFields={canDelete}
        canEditCustomFields={canEdit}
        onClick={onClick}
        field={field}
        {...rest}
      />
    );
  },
);

PrivateAssetPanelCustomFieldContainer.displayName = 'PrivateAssetPanelCustomFieldContainer';
